import {
  CalendarDaysIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  EnvelopeIcon,
  HashtagIcon,
  MapPinIcon,
  PhoneIcon,
  TagIcon,
  UserIcon
} from '@heroicons/react/24/outline'
import { format } from 'date-fns'
import { de, enUS as en } from 'date-fns/locale'
import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import constants_work_experience from '../../constants/work-experience'
import { useLanguageContext } from '../../context'
import { mutation } from '../../graphql'
import { parseAdditionalData } from '../../helpers/talent-details'
import { useCreateLog } from '../../hooks/use-create-log'
import Badge from '../tailwind/Badge'
import CollapsibleSection from '../tailwind/CollapsibleSection'
import DetailField from '../tailwind/DetailField'
import GroupLabel from '../tailwind/GroupLabel'
import Panel from '../tailwind/Panel'
import SectionHeading from '../tailwind/SectionHeading'
import SpaceDetailItem from './space-detail-item'

const Group = ({ title, children }) => (
  <div className='py-6'>
    <GroupLabel>{title}</GroupLabel>
    <dl className='mt-4 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2'>
      {children}
    </dl>
  </div>
)

Group.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
}

const SpaceDetailsProfile = ({ space, refetchSpace }) => {
  const { t } = useTranslation()
  const { language } = useLanguageContext()
  const { createSpaceHistoryLog } = useCreateLog()

  const { firstname, lastname, email, partner_user_id, createdAt } = space
  const phone = space.phone || space.person?.phone
  const postcode = space.person?.postcode
  const city = space.person?.city

  const { workExperience, fields } = parseAdditionalData(space)

  // Berufserfahrung: "Mid-level (3 bis 5 Jahre …)" -> Badge "Mid-level" + Caption
  let weLevel = ''
  let weCaption
  if (workExperience !== null && constants_work_experience[workExperience]) {
    const weLabels = constants_work_experience[workExperience].label
    const weLabel = weLabels[language] || weLabels.de
    const [level, rest] = weLabel.split(' (')
    weLevel = level
    weCaption = rest ? rest.replace(/\)$/, '') : undefined
  }

  const initialFirstname =
    space.person?.firstname || space.person?.invitation_firstname || ''
  const initialLastname =
    space.person?.lastname || space.person?.invitation_lastname || ''

  const formik = useFormik({
    initialStatus: {},
    enableReinitialize: true,
    initialValues: {
      firstname: initialFirstname,
      lastname: initialLastname
    },
    validationSchema: Yup.object({
      firstname: Yup.string(),
      lastname: Yup.string()
    }),
    onSubmit: ({ firstname: fn, lastname: ln }) => {
      // Person vorhanden -> updaten; sonst neu anlegen (person.id === space.id
      // via `Space.person @hasOne(fields:["id"])`, daher kein extra Link nötig).
      const personMutation = space.person?.id
        ? {
            mutation: 'updatePerson',
            input: { id: space.person.id, firstname: fn, lastname: ln }
          }
        : {
            mutation: 'createPerson',
            input: {
              id: space.id,
              firstname: fn,
              lastname: ln,
              owner: space.owner,
              authorized_create: space.authorized_create,
              authorized_read: space.authorized_read,
              authorized_update: space.authorized_update,
              authorized_delete: space.authorized_delete
            }
          }

      // Feld-Diff für den Verlauf (nur tatsächlich geänderte Felder)
      const changes = []
      // label_key statt aufgelöstem Text speichern: der Verlauf übersetzt das Label
      // erst beim Rendern in der Sprache des Betrachters (space-history.js); das
      // aufgelöste label bleibt als Fallback für ältere Renderer-Stände dabei.
      if (fn !== initialFirstname) {
        changes.push({
          label_key: 'pages.talent.space_details_firstname_label',
          label: t('pages.talent.space_details_firstname_label'),
          before: initialFirstname || '—',
          after: fn || '—'
        })
      }
      if (ln !== initialLastname) {
        changes.push({
          label_key: 'pages.talent.space_details_lastname_label',
          label: t('pages.talent.space_details_lastname_label'),
          before: initialLastname || '—',
          after: ln || '—'
        })
      }

      Promise.all([
        mutation(personMutation),
        // update space.updatedAt
        mutation({ mutation: 'updateSpace', input: { id: space.id } })
      ]).then(() => {
        // Recruiter-Edit im Verlauf dokumentieren. Fire-and-forget: ein
        // fehlgeschlagener Log darf das Speichern nicht brechen. Der Refetch
        // wartet auf den Log, damit der neue Verlaufseintrag direkt erscheint.
        const logChanges = changes.length
          ? createSpaceHistoryLog({
              space_id: space.id,
              career_id: space.career_id,
              context: 'SYSTEM',
              status: 'APPLICATION_DATA_UPDATED_BY_PARTNER',
              changes
            }).catch((err) => console.error(err))
          : Promise.resolve()

        return logChanges.then(refetchSpace)
      })
    }
  })

  const hasContact = !!email || !!phone || !!postcode || !!city
  const postcodeValue = [postcode, city].filter(Boolean).join(' ')

  return (
    <Panel>
      <section aria-labelledby='applicant-information-title'>
        <SectionHeading
          id='applicant-information-title'
          title={t('pages.talent.space_details_profile_header')}
          description={t('pages.talent.space_details_profile_description')}
        />

        <div className='divide-y divide-gray-200 border-t border-gray-300'>
          {/* Kontakt */}
          {hasContact && (
            <Group title={t('pages.talent.group_contact')}>
              {!!email && (
                <DetailField
                  icon={EnvelopeIcon}
                  label={t('pages.talent.space_details_email_label')}
                  value={email}
                  href={`mailto:${email}`}
                />
              )}
              {!!phone && (
                <DetailField
                  icon={PhoneIcon}
                  label={t('pages.talent.phone_label')}
                  value={phone}
                  href={`tel:${phone}`}
                />
              )}
              {!!postcodeValue && (
                <DetailField
                  icon={MapPinIcon}
                  label={t('pages.talent.postcode_label')}
                  value={postcodeValue}
                />
              )}
            </Group>
          )}

          {/* Zur Person */}
          <Group title={t('pages.talent.group_person')}>
            <SpaceDetailItem
              icon={UserIcon}
              term={t('pages.talent.space_details_firstname_label')}
              details={firstname}
              input={{ id: 'firstname', formik }}
            />
            <SpaceDetailItem
              icon={UserIcon}
              term={t('pages.talent.space_details_lastname_label')}
              details={lastname}
              input={{ id: 'lastname', formik }}
            />
            <DetailField
              icon={CalendarDaysIcon}
              label={t('talent.state_invited')}
              value={format(new Date(createdAt), 'd. MMMM yyyy', {
                locale: { de, en }[language]
              })}
            />
            {weLevel && (
              <DetailField
                icon={ClockIcon}
                label={t('pages.talent.work_experience_label')}
                value={weLevel}
                badge
                badgeColor='indigo'
                caption={weCaption}
              />
            )}
          </Group>

          {/* Weitere Angaben (vom Arbeitgeber angefragte Felder) */}
          {fields.length > 0 && (
            <Group title={t('pages.talent.group_additional')}>
              {fields.map((field) =>
                field.type === 'dropdown' ? (
                  <DetailField
                    key={field.id}
                    icon={TagIcon}
                    label={field.label}
                  >
                    <div className='flex flex-wrap gap-1.5'>
                      {(field.multiple
                        ? field.value.split(', ').filter(Boolean)
                        : [field.value]
                      ).map((v, i) => (
                        <Badge
                          key={i}
                          text={v}
                          color='blue'
                          type='basic'
                          wrap
                        />
                      ))}
                    </div>
                  </DetailField>
                ) : (
                  <DetailField
                    key={field.id}
                    icon={ClipboardDocumentListIcon}
                    label={field.label}
                    value={field.value}
                  />
                )
              )}
            </Group>
          )}

          {/* Technische Details (eingeklappt) */}
          <CollapsibleSection title={t('pages.talent.technical_details')}>
            <dl className='grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2'>
              <DetailField
                icon={HashtagIcon}
                label={t('pages.talent.assessment_id_label')}
                value={space.id}
                copyable
              />
              {!!space.owner && (
                <DetailField
                  icon={HashtagIcon}
                  label={t('pages.talent.owner_id_label')}
                  value={space.owner}
                  copyable
                />
              )}
              {!!partner_user_id && (
                <DetailField
                  icon={HashtagIcon}
                  label={t('pages.talent.space_details_partner_user_id_label')}
                  value={partner_user_id}
                  copyable
                />
              )}
            </dl>
          </CollapsibleSection>
        </div>
      </section>
    </Panel>
  )
}

SpaceDetailsProfile.propTypes = {
  space: PropTypes.object.isRequired,
  refetchSpace: PropTypes.func.isRequired
}

export default SpaceDetailsProfile
